import { LitElement, paths, html, unsafeHTML, css, utils } from '@hh/global.js'
const { externalVideoEmbedSrc, getPreviousSibling } = utils

class AdaptiveVideo extends LitElement {
  static properties = {
    videoObject: {
      type: Object,
      attribute: 'video-object',
    },
    videoObjectMobile: {
      type: Object,
      attribute: 'video-object-mobile',
    },
    id: {
      type: String,
    },
    title: {
      type: String,
    },
    autoplay: {
      type: Boolean,
    },
    controls: {
      type: Boolean,
    },
    loop: {
      type: Boolean,
    },
    audio: {
      type: Boolean,
    },
    host: {
      type: String,
    },
    description: {
      type: String,
    },
    showAudioControl: {
      type: Boolean,
      attribute: 'show-audio-control',
    },
    aspectRatio: {
      type: String,
      attribute: 'aspect-ratio',
    },
    objectFit: {
      type: String,
      attribute: 'object-fit',
    },
    posterImage: {
      type: String,
      attribute: 'poster-image',
    },
    preload: {
      type: Boolean,
    },
  }

  get computedAspectRatio() {
    return (
      this.videoObject?.aspect_ratio ||
      (this.aspectRatio === 'wide' && 16 / 9) ||
      (this.aspectRatio === 'full' && 4 / 3) ||
      eval(this.aspectRatio) ||
      16 / 9
    )
  }

  get videoElement() {
    return this.renderRoot?.getElementById('video-element') ?? null
  }

  constructor() {
    super()

    this.objectFit = 'cover'
    this.muted = false;
    this.autoplayed = false;
  }

  connectedCallback() {
    super.connectedCallback()
    this.playing = this.autoplay
    this.style.setProperty('--hh-aspect-ratio', this.computedAspectRatio)
    if (this.videoObjectMobile) {
      this.style.setProperty(
        '--hh-aspect-ratio-mobile',
        this.videoObjectMobile?.aspect_ratio || this.computedAspectRatio
      )
    }
    this.isMobileDevice = this.isMobile()
    window.addEventListener('resize', this.onResize.bind(this))

    this.style.setProperty('--hh-object-fit', this.objectFit)
  }

  isMobile() {
    return window.matchMedia('(max-width: 749px)').matches
  }

  onResize() {
    const isMobile = this.isMobile()
    if (this.playing && this.isMobileDevice != isMobile && this.videoObjectMobile) {
      this.videoElement.load()
      this.videoElement.play()
      this.playing = true
      this.isMobileDevice = isMobile
    }
  }

  firstUpdated() {
    if (this.host === 'vimeo') {
      this.vPlayer = new Vimeo(this.videoElement)
    } else if (this.host === 'youtube') {
      this.ytPlayer = YouTubePlayer(this.videoElement)
    }

    this.videoElement.addEventListener('ended', (e) => {
      this.dispatchEvent(
        new CustomEvent('videoStatus', {
          bubbles: true,
          composed: true,
          detail: {
            playing: () => false,
          },
        })
      )

      overlay.classList.remove('playing')
    })

    this.addEventListener(
      'customVideoEvent',
      this.handleCustomVideoEvent.bind(this)
    )

    this.addEventListener(
      'customAudioEvent',
      this.handleAudioControlClick.bind(this)
    )
  }

  handleCustomVideoEvent() {
    const overlay = this.renderRoot?.querySelector('.video-overlay')

    if (!this.playing) {
      this.play()
    } else {
      this.pause()
    }

    this.dispatchEvent(
      new CustomEvent('videoStatus', {
        bubbles: true,
        composed: true,
        detail: {
          playing: () => this.playing,
        },
      })
    )
  }

  handlePlayClick() {
    if (!this.playing) {
      this.play()
    } else {
      this.pause()
    }

    this.dispatchEvent(
      new CustomEvent('videoStatus', {
        bubbles: true,
        composed: true,
        detail: {
          playing: () => this.playing,
        },
      })
    )
  }

  play() {
    this.playing = true
    this.renderRoot?.querySelector('.video-overlay').classList.add('playing')

    if (this.audio && !this.muted) {
      this.unmute()
    } else {
      this.mute()
    }

    if (this.host === 'youtube') {
      this.ytPlayer?.playVideo()
    } else if (this.host === 'vimeo') {
      this.vPlayer?.play()
    } else {
      this.videoElement.play()
    }
  }

  pause() {
    this.playing = false
    this.renderRoot?.querySelector('.video-overlay')?.classList.remove('playing')

    if (this.host === 'youtube') {
      this.ytPlayer?.pauseVideo()
    } else if (this.host === 'vimeo') {
      this.vPlayer?.pause()
    } else {
      this.videoElement.pause()
    }
  }

  mute() {
    if (this.host === 'youtube') {
      this.ytPlayer?.mute()
    } else if (this.host === 'vimeo') {
      this.vPlayer?.setMuted(true)
    } else {
      this.videoElement.muted = true
    }
  }

  unmute() {
    if (this.host === 'youtube') {
      this.ytPlayer?.unMute()
    } else if (this.host === 'vimeo') {
      this.vPlayer?.setMuted(false)
    } else {
      this.videoElement.removeAttribute('autoplay')
      this.videoElement.muted = false
    }
  }

  generatePosterWithText() {
    return html`
      <div class="video-overlay ${this.autoplay ? 'playing' : ''}">
        ${this.title ? html`<h2>${this.title}</h2>` : html``}
        ${this.description ? html`<p>${this.description}</p>` : html``}
        <button
          class="video-poster-button"
          @click="${this.handlePlayClick}"
          aria-label="Play or pause video"
        >
          ${unsafeHTML(paths.play || html`►`)}
          ${unsafeHTML(paths.pause || html`||`)}
        </button>
        ${this.posterImage ? html`<img src="${this.posterImage}" loading="lazy" />` : html``}
      </div>
    `
  }

  handleAudioControlClick() {
    if (this.muted) {
      this.querySelector('.audio-cta')?.classList.remove('muted')
      this.muted = false
      this.unmute()
    } else {
      this.querySelector('.audio-cta')?.classList.add('muted')
      this.muted = true
      this.mute()
    }
  }

  generateAudioButton() {
    return html`<div
      class="audio-control"
      @click="${this.handleAudioControlClick}"
    >
      <slot name="audio"></slot>
    </div>`
  }

  renderVideo() {
    return html`
      <video
        id="video-element"
        playsinline="playsinline"
        ?autoplay="${this.autoplay}"
        ?muted="${this.autoplay}"
        ?controls="${this.controls}"
        ?loop="${this.loop}"
        allow="autoplay"
        ${this.posterImage ? `poster="${this.posterImage}"` : html``}
        preload="${this.preload ? `metadata` : `none`}"
        muted="muted"
        aria-label="${this.description}"
      >
        ${this.videoObjectMobile?.sources.map(
          (source) =>
            html`<source src="${source.url}#t=0.5" type="${source.mime_type}" media="(max-width: 749px)"></source>`
        )}
        ${this.videoObject.sources.map(
          (source) =>
            html`<source src="${source.url}#t=0.5" type="${source.mime_type}"></source>`
        )}
      </video>
    `
  }

  renderIframe() {
    return html`
      <iframe
        id="video-element"
        frameborder="0"
        allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
        allowfullscreen="allowfullscreen"
        loading="lazy"
        src="${externalVideoEmbedSrc(
          this.host,
          this.id,
          this.autoplay,
          this.controls,
          this.loop,
          this.audio
        )}"
        title="${this.title}"
      >
      </iframe>
    `
  }

  render() {
    return html`
      ${['vimeo', 'youtube'].includes(this.host)
        ? this.renderIframe()
        : this.renderVideo()}
      ${this.generatePosterWithText()}
      ${this.showAudioControl && this.generateAudioButton()}
    `
  }

  static styles = css`
    :host {
      aspect-ratio: var(
        --hh-aspect-ratio-mobile,
        var(--hh-aspect-ratio, 16 / 9)
      );
      display: block;
      position: relative;
    }
    @media (min-width: 750px) {
      :host {
        aspect-ratio: var(--hh-aspect-ratio, 16 / 9);
      }
    }
    #video-element {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      width: 100%;
      height: 100%;
      object-fit: var(--hh-object-fit, contain);
    }
    .video-overlay {
      position: absolute;
      display: flex;
      top: 0;
      width: 100%;
      height: 100%;
      flex-direction: column;
      align-items: center;
      justify-content: center;
    }
    h2,
    p {
      z-index: 1;
      color: white;
    }
    p {
      width: 25%;
      text-align: center;
      margin-bottom: 3rem;
    }
    .video-overlay img {
      position: absolute;
      top: 0;
      width: 100%;
      height: 100%;
      filter: brightness(0.5);
    }
    .video-overlay.playing img {
      display: none;
    }
    .video-poster-button {
      align-items: center;
      background: transparent;
      border: none;
      cursor: pointer;
      width: 100%;
      height: 100%;
      padding: 1rem;
      display: flex;
      justify-content: center;
      z-index: 1;
      color: white;
    }
    .video-poster-button:hover {
      color: black;
    }
    .video-poster-button svg {
      position: relative;
      width: 85px;
      height: 75px;
      fill: white;
    }

    .video-overlay.playing .video-poster-button svg.icon-play {
      display: none;
    }

    .video-overlay:not(.playing) .video-poster-button svg.icon-pause {
      display: none;
    }

    .video-overlay.playing .video-poster-button svg.icon-pause {
      display: none;
      color: #fff;
      fill: #fff;
    }

    .video-overlay.playing:hover .video-poster-button svg.icon-pause {
      display: flex;
    }

    .audio-control {
      position: absolute;
      bottom: 10px;
      right: 10px;
      z-index: 999;
      width: 40px;
      height: 40px;
      display: flex;
      align-items: center;
      justify-content: flex-start;
    }
    @media (min-width: 750px) {
      .video-poster-button svg {
        width: 122px;
        height: 108px;
      }
    }
  `
}

window.customElements.define('hh-adaptive-video', AdaptiveVideo)
